import { DateTime } from 'luxon';
import {
  buildPersonCard, CardSeverity, classifyBaseBand, FiredSignal, formatSignalValue, NULL_SIGNAL_LINK_BUILDER, PersonCard,
  PersonSignalType, SignalLinkBuilder, signalTypeLabel
} from './person-signal-card';

/**
 * AA-225 — View details, per user (week matrix). Pure data/logic only, same split as
 * person-signal-card.ts: no Angular dependency, so every rule here is unit-testable on its own.
 *
 * A day's evaluated / not-scheduled / leave state still arrives as data. The chart week is separate:
 * company settings `firstDayOfWeek` plus the next four days. The other two are the weekend — grey
 * in View details, and never a peak — which is Saturday/Sunday only when the company starts Monday.
 */

export type DayState = 'evaluated' | 'not_scheduled' | 'on_leave';

/** One signal type's value for one day. Present only for the signals the card actually shows —
 * see PersonWeekEvidence.signals: a signal the sensitivity notch filtered off the card never gets
 * a value here either (3.2.1b — this screen shows exactly the card's signals, never a superset). */
export interface PersonWeekDayValue {
  type: PersonSignalType;
  value: number; // native unit — percent for idle_time, decimal hours for the rest
  // The gateway's own formatted text for a breaching day (e.g. "1h 53m"). It truncates minutes where
  // our formatter rounds, so showing it keeps the cell identical to the API; absent otherwise.
  display?: string;
  href?: string; // the gateway's own link for this exact day; absent when the day carried no issue
}

export interface PersonWeekDay {
  date: string; // ISO yyyy-mm-dd, this user's own calendar day (already zone-resolved upstream)
  weekday: string; // 'Mon'..'Sun' — display label only, carried as data, never derived positionally
  state: DayState;
  // Present (possibly []) even when state !== 'evaluated' — a non-working day still has a tracked-
  // hours number for the Tracked total and the "No tracking" vs a real number distinction; it just
  // never carries a fired value (a signal is never flagged on a non-working day, spec 3.2.4).
  values: PersonWeekDayValue[];
  trackedHours: number; // 0 is a legitimate value on a non-working day ("No tracking")
}

export interface PersonWeekEvidence {
  userId: string;
  userName: string;
  timezone: string; // IANA id — the same zone the header's offset label is built from
  // The recurring working-day pattern (e.g. ['Mon',...,'Fri'] or ['Tue',...,'Sat']), arrives as
  // data from Layer 1 scheduled hours (spec 3.2.4). Used only for the count line's day-name range
  // — never to decide any individual day's state, which is `days[].state` below.
  workingWeekdays: string[];
  days: PersonWeekDay[]; // every day in range, in calendar order, working and non-working alike
  signals: FiredSignal[]; // exactly the card's visible signals — see buildPersonWeekCard
}

/** Builds the same PersonCard the Needs you feed already shows for this user, from this fixture's
 * own signals — so the detail screen's title/severity/signal-count/order can never disagree with
 * the card that linked here. `buildLink` is optional; omit it for plain (non-linking) previews. */
export function buildPersonWeekCard(evidence: PersonWeekEvidence, weekStart?: Date,
  buildLink: SignalLinkBuilder = NULL_SIGNAL_LINK_BUILDER): PersonCard | null {
  return buildPersonCard(evidence.userId, evidence.userName, evidence.signals, weekStart, buildLink);
}

export function evaluatedDays(evidence: PersonWeekEvidence): PersonWeekDay[] {
  return evidence.days.filter(d => d.state === 'evaluated');
}

/** Always in day order — NOT always the week's tail. A non-working Wednesday can sit below the
 * divider next to Saturday, so this is a filter over `days` (already in calendar order), never a
 * slice off the end. */
export function nonEvaluatedDays(evidence: PersonWeekEvidence): PersonWeekDay[] {
  return evidence.days.filter(d => d.state !== 'evaluated');
}

const WEEKDAY_SEQUENCE = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Five chart days from the company week start. `firstDayOfWeek` matches company settings:
 * 0 or 7 = Sunday, 1 = Monday … 6 = Saturday. Anything else falls back to Monday.
 */
export function companyChartWeekdays(firstDayOfWeek = 1): string[] {
  const start = firstDayOfWeek === 0 || firstDayOfWeek === 7 ? 6
    : firstDayOfWeek >= 1 && firstDayOfWeek <= 6 ? firstDayOfWeek - 1
    : 0;
  return Array.from({ length: 5 }, (_, offset) => WEEKDAY_SEQUENCE[(start + offset) % 7]);
}

/**
 * companyChartWeekdays minus today and any later day, so a week still in progress never draws a
 * column for a day that hasn't finished (AA-287). `weekStart` is the week's first date (ISO); each
 * chart weekday resolves to its date inside the seven days from it, and "today" is read in
 * `timezone` (the company's — the zone the week itself is keyed in). Fails open to every chart day
 * when `weekStart` or `timezone` can't be resolved, rather than hiding a real week.
 */
export function elapsedChartWeekdays(
  weekStart: string, firstDayOfWeek = 1, timezone?: string, now: DateTime = DateTime.now(),
): string[] {
  const chartDays = companyChartWeekdays(firstDayOfWeek);
  const today = now.setZone(timezone || 'utc').toISODate();
  const start = DateTime.fromISO(weekStart, { zone: 'utc' });
  if (!today || !start.isValid) return chartDays;
  const dateByWeekday = new Map<string, string>();
  for (let offset = 0; offset < 7; offset++) {
    const day = start.plus({ days: offset });
    dateByWeekday.set(WEEKDAY_SEQUENCE[day.weekday - 1], day.toISODate() ?? '');
  }
  return chartDays.filter(weekday => (dateByWeekday.get(weekday) ?? '') < today);
}

/** The two days the chart does not draw for this company. */
export function isCompanyWeekend(weekday: string, firstDayOfWeek = 1): boolean {
  return !companyChartWeekdays(firstDayOfWeek).includes(weekday);
}

/** "Mon to Fri" / "Tue to Sat" for a contiguous pattern (every real case), falling back to a plain
 * comma list for a non-contiguous one so nothing throws — but every fixture in this feature is
 * contiguous, per the spec's own examples. Generated from `workingWeekdays`, never from position. */
export function evaluatedRangeLabel(workingWeekdays: string[]): string {
  if (workingWeekdays.length === 0) return '';
  if (workingWeekdays.length === 1) return workingWeekdays[0];
  const indices = workingWeekdays.map(d => WEEKDAY_SEQUENCE.indexOf(d)).sort((a, b) => a - b);
  const isContiguous = indices.every((idx, i) => i === 0 || idx === indices[i - 1] + 1);
  if (isContiguous) return `${WEEKDAY_SEQUENCE[indices[0]]} to ${WEEKDAY_SEQUENCE[indices[indices.length - 1]]}`;
  return workingWeekdays.join(', ');
}

/**
 * Matrix columns, in the SAME order as the card title/body (severity desc, then N desc, then the
 * fixed PERSON_SIGNAL_TYPES tie-break — see orderSignalsForCard). `heavy_hours` always renders as
 * the "Hours tracked" column even when it isn't one of the card's fired types, since it's the
 * denominator behind idle percentage and appears on every card (spec 3.2) — in that case it's
 * appended after the fired columns, unthresholded and never chipped. This is a judgment call: the
 * spec doesn't state where an unfired Hours tracked column sits when heavy_hours never fired.
 */
export function weekMatrixColumns(card: PersonCard): PersonSignalType[] {
  if (card.orderedTypes.includes('heavy_hours')) return card.orderedTypes;
  return [...card.orderedTypes, 'heavy_hours'];
}

/** Per-day severity band for one signal's value — the same base-band math that feeds the card's
 * rolled-up severity (classifyBaseBand), just evaluated against one day's value instead of the
 * week's peak. This is what tints a day's chip (spec 3.2.1: "the per-day band, not the signal's
 * rolled-up severity"). */
export function dayBand(value: number, sig: FiredSignal): CardSeverity {
  return classifyBaseBand(value, sig.threshold, sig.x, sig.y, sig.direction ?? 'above');
}

export function isOverThreshold(value: number, sig: FiredSignal): boolean {
  return (sig.direction ?? 'above') === 'above' ? value >= sig.threshold : value <= sig.threshold;
}

/** True only when this exact (day, type) pair is where the FiredSignal recorded its week peak —
 * and even then only if the signal fired on 2+ days (spec 3.2.2: a single flagged day is
 * self-evidently the peak, so the marker is redundant and the card copy already drops it). */
export function isPeakCell(
  evidence: PersonWeekEvidence, day: PersonWeekDay, type: PersonSignalType, firstDayOfWeek = 1,
): boolean {
  if (day.state !== 'evaluated') return false; // spec 3.2.4 — the peak is drawn from evaluated days only
  if (isCompanyWeekend(day.weekday, firstDayOfWeek)) return false;
  const sig = evidence.signals.find(s => s.type === type);
  if (!sig || sig.n < 2) return false;
  return day.weekday === sig.peakDay;
}

function valueFor(day: PersonWeekDay, type: PersonSignalType): number {
  return day.values.find(v => v.type === type)?.value ?? 0;
}

/**
 * Baseline row for one column. Idle percentage is weighted by tracked time so it reconciles
 * against Activity Summary (spec 3.2.3) — an unweighted mean of daily percentages does not. Every
 * other duration-native signal is a plain mean of its daily values; both use the evaluated days
 * only, never all 7 and never just the days with data, so the denominator matches the card's `D`.
 */
export function weekAverage(evidence: PersonWeekEvidence, type: PersonSignalType): number {
  const days = evaluatedDays(evidence);
  if (days.length === 0) return 0;
  if (type === 'idle_time') {
    const totalTrackedHours = days.reduce((sum, d) => sum + d.trackedHours, 0);
    if (totalTrackedHours === 0) return 0;
    const idleHours = days.reduce((sum, d) => sum + (valueFor(d, type) / 100) * d.trackedHours, 0);
    return (idleHours / totalTrackedHours) * 100;
  }
  const total = days.reduce((sum, d) => sum + valueFor(d, type), 0);
  return total / days.length;
}

/** Every day shown, weekend hours included — this is the accuracy number, and payroll counts
 * Saturday (spec 3.2.4). Unlike weekAverage, this deliberately does NOT filter to evaluated days. */
export function trackedTotal(evidence: PersonWeekEvidence): number {
  return evidence.days.reduce((sum, d) => sum + d.trackedHours, 0);
}

function capitalize(band: CardSeverity): string {
  return band.charAt(0).toUpperCase() + band.slice(1);
}

/**
 * Header's one severity-explainer line (spec 3.1), built from the card's LEADING signal (the same
 * one `orderSignalsForCard` puts first — severity desc, then N desc). Three shapes, matching the
 * spec's templates exactly:
 *   - single-day cap: only when N===1 actually pulled a Critical base band down to Warning —
 *     if N===1 never changed the band (it was already Info/Warning), the plain sentence below is
 *     the true one, since the cap was a no-op.
 *   - escalation: the persistence rule (N/D >= 0.5) moved the band up a step.
 *   - otherwise: the full "{Band} because {type} went over on {N} of {D} days, peaking at {peak}
 *     on {peakDay}." sentence.
 */
export function severityExplainerLine(card: PersonCard, signals: FiredSignal[]): string {
  const leadingType = card.orderedTypes[0];
  const sig = signals.find(s => s.type === leadingType);
  if (!sig) return '';
  const base = classifyBaseBand(sig.peak, sig.threshold, sig.x, sig.y, sig.direction ?? 'above');
  const final = card.severities[leadingType];

  if (sig.n === 1 && base === 'critical') {
    return 'Capped at Warning because only one day went over.';
  }
  if (final !== base) {
    return `Escalated to ${capitalize(final)} because ${sig.n} of ${sig.d} days went over.`;
  }
  const peakLabel = formatSignalValue(leadingType, sig.peak);
  return `${capitalize(final)} because ${signalTypeLabel(leadingType)} went over on ${sig.n} of ${sig.d} days, `
    + `peaking at ${peakLabel} on ${sig.peakDay}.`;
}
